export type ThemeMode = 'site' | 'app';

export interface SiteRuntimeConfig {
    modules: string[];
    theme: ThemeMode;
    appThemeId?: string;
}

export interface ShellRuntimeConfig {
    siteId?: string;
    sites: Record<string, SiteRuntimeConfig>;
}

const RUNTIME_CONFIG_URL = `${process.env.PUBLIC_URL}/site-configs/runtime-shell.json`;
const MODULES_ALL_TOKEN = 'all';
const REQUIRED_MODULE_KEYS = ['home'];

const DEFAULT_SHELL_RUNTIME_CONFIG: ShellRuntimeConfig = {
    sites: {
        default: {
            modules: ['it-dev-fallback'],
            theme: 'site',
            appThemeId: 'default',
        },
        showcase: {
            modules: ['home', 'store', 'services', 'about', 'contact'],
            theme: 'site',
            appThemeId: 'default',
        },
        lamante: {
            modules: [MODULES_ALL_TOKEN],
            theme: 'site',
            appThemeId: 'default',
        },
        coffeehouse: {
            modules: [
                'home',
                'store',
                'admin-panel',
                'account',
                'contact',
                'imprint',
                'privacy',
                'cookies',
                'auto-login',
            ],
            theme: 'site',
            appThemeId: 'default',
        },
    },
};

let shellRuntimeConfig: ShellRuntimeConfig = DEFAULT_SHELL_RUNTIME_CONFIG;

const isThemeMode = (value: unknown): value is ThemeMode => value === 'site' || value === 'app';

const normalizeModuleKeys = (modules: string[]): string[] =>
    Array.from(new Set(modules.map(moduleKey => moduleKey.trim()).filter(moduleKey => moduleKey.length > 0)));

const ensureRequiredModules = (modules: string[], availableModules: string[]): string[] => {
    const moduleSet = new Set(modules);

    REQUIRED_MODULE_KEYS.forEach(requiredModule => {
        if (availableModules.includes(requiredModule)) {
            moduleSet.add(requiredModule);
        }
    });

    return Array.from(moduleSet);
};

const resolveConfiguredModules = (
    configuredModules: string[],
    availableModules: string[],
    fallbackToAllAvailable: boolean
): string[] => {
    const normalizedConfigured = normalizeModuleKeys(configuredModules);
    if (normalizedConfigured.includes(MODULES_ALL_TOKEN)) {
        return ensureRequiredModules(availableModules, availableModules);
    }

    const filteredConfigured = normalizedConfigured.filter(moduleKey => availableModules.includes(moduleKey));
    if (filteredConfigured.length > 0) {
        return ensureRequiredModules(filteredConfigured, availableModules);
    }

    return fallbackToAllAvailable
        ? ensureRequiredModules(availableModules, availableModules)
        : ensureRequiredModules([], availableModules);
};

const sanitizeSiteConfig = (siteConfig: unknown, fallback?: SiteRuntimeConfig): SiteRuntimeConfig => {
    const fallbackTheme = fallback?.theme ?? 'site';
    const fallbackModules = fallback?.modules ?? [];
    const fallbackAppThemeId = fallback?.appThemeId ?? 'default';

    if (!siteConfig || typeof siteConfig !== 'object') {
        return {
            modules: fallbackModules,
            theme: fallbackTheme,
            appThemeId: fallbackAppThemeId,
        };
    }

    const candidate = siteConfig as Partial<SiteRuntimeConfig>;
    const modules = Array.isArray(candidate.modules)
        ? candidate.modules.filter((moduleKey): moduleKey is string => typeof moduleKey === 'string')
        : fallbackModules;

    return {
        modules,
        theme: isThemeMode(candidate.theme) ? candidate.theme : fallbackTheme,
        appThemeId: typeof candidate.appThemeId === 'string' ? candidate.appThemeId : fallbackAppThemeId,
    };
};

const sanitizeRuntimeConfig = (runtimeConfig: unknown): ShellRuntimeConfig => {
    if (!runtimeConfig || typeof runtimeConfig !== 'object') {
        return DEFAULT_SHELL_RUNTIME_CONFIG;
    }

    const candidate = runtimeConfig as Partial<ShellRuntimeConfig>;
    const fallbackSites = DEFAULT_SHELL_RUNTIME_CONFIG.sites;
    const candidateSites =
        candidate.sites && typeof candidate.sites === 'object'
            ? (candidate.sites as Record<string, unknown>)
            : undefined;
    const siteIds = new Set<string>([
        ...Object.keys(fallbackSites),
        ...(candidateSites ? Object.keys(candidateSites) : []),
    ]);

    const mergedSites = Array.from(siteIds).reduce<Record<string, SiteRuntimeConfig>>((acc, siteId) => {
        acc[siteId] = sanitizeSiteConfig(candidateSites?.[siteId], fallbackSites[siteId]);
        return acc;
    }, {});

    return {
        siteId: typeof candidate.siteId === 'string' ? candidate.siteId : undefined,
        sites: mergedSites,
    };
};

export const loadShellRuntimeConfig = async (): Promise<void> => {
    try {
        const response = await fetch(RUNTIME_CONFIG_URL, { cache: 'no-store' });
        if (!response.ok) {
            shellRuntimeConfig = DEFAULT_SHELL_RUNTIME_CONFIG;
            return;
        }

        const runtimeConfig: unknown = await response.json();
        shellRuntimeConfig = sanitizeRuntimeConfig(runtimeConfig);
    } catch (_error) {
        shellRuntimeConfig = DEFAULT_SHELL_RUNTIME_CONFIG;
    }
};

export const getRuntimePreferredSiteId = (): string | undefined => shellRuntimeConfig.siteId;

export const getSiteRuntimeConfig = (siteId: string, defaultModules: string[]): SiteRuntimeConfig => {
    const availableModules = normalizeModuleKeys(defaultModules);
    const runtimeSiteConfig = shellRuntimeConfig.sites[siteId];
    const fallbackSiteConfig = DEFAULT_SHELL_RUNTIME_CONFIG.sites[siteId];

    if (runtimeSiteConfig) {
        return {
            modules: resolveConfiguredModules(runtimeSiteConfig.modules, availableModules, false),
            theme: runtimeSiteConfig.theme,
            appThemeId: runtimeSiteConfig.appThemeId,
        };
    }

    if (fallbackSiteConfig) {
        return {
            modules: resolveConfiguredModules(fallbackSiteConfig.modules, availableModules, true),
            theme: fallbackSiteConfig.theme,
            appThemeId: fallbackSiteConfig.appThemeId,
        };
    }

    return {
        modules: availableModules,
        theme: 'site',
        appThemeId: 'default',
    };
};
